/**
 * Access governance — TanStack Query hooks.
 *
 * Read endpoints: queue, thread, decisions, attestations,
 * routing policy.
 *
 * Decision mutations are kind-specific (recovery + access have
 * different post-decision actions); they live in their
 * respective feature folders. The queue page uses thin wrappers
 * here to dispatch to the right kind.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import {
  attestationRowSchema,
  decisionRowSchema,
  jitHealthSchema,
  queueResponseSchema,
  threadCommentSchema,
  type AttestationMethod,
  type AttestationRow,
  type DecisionKind,
  type DecisionRow,
  type HistoryFilter,
  type QueueFilter,
  type QueueResponse,
  type RequestKind,
  type ThreadComment,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/access-governance`;
}

const PAGE_SIZE = 50;

function buildQueueQS(filter: QueueFilter): string {
  const p = new URLSearchParams();
  if (filter.kind) p.set("kind", filter.kind);
  if (filter.priority) p.set("priority", filter.priority);
  if (filter.sla) p.set("sla", filter.sla);
  // Default view shows OPEN requests only (pending + awaiting_info);
  // status="all" lifts the restriction.
  if (filter.status !== "all") p.set("only_awaiting", "true");
  const page = filter.page ?? 1;
  p.set("limit", String(PAGE_SIZE));
  p.set("offset", String((page - 1) * PAGE_SIZE));
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchQueue(
  tenantID: string,
  filter: QueueFilter,
): Promise<QueueResponse> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/queue${buildQueueQS(filter)}`);
  return queueResponseSchema.parse(raw);
}

// ── History (decided/closed requests) ─────────────────────────────────────

export const HISTORY_PAGE_SIZE = PAGE_SIZE;

// buildHistoryParams maps the URL-driven HistoryFilter onto the backend's
// queue/export query params. `only_decided=true` is the default universe
// (terminal rows only); a pinned `status` narrows to one terminal status. The
// yyyy-mm-dd `from`/`to` expand to an INCLUSIVE RFC3339 day range so a "to" of
// Jun-17 includes everything decided on Jun-17 (not just 00:00:00).
function buildHistoryParams(filter: HistoryFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (filter.kind) p.set("kind", filter.kind);
  if (filter.status) {
    p.set("status", filter.status);
  } else {
    p.set("only_decided", "true");
  }
  if (filter.from) p.set("from", `${filter.from}T00:00:00Z`);
  // End-of-day to the last representable instant so the bound covers the WHOLE
  // selected day — timestamptz is sub-second, so a plain T23:59:59Z would drop a
  // request filed at 23:59:59.5 on the boundary day from the view AND the export.
  if (filter.to) p.set("to", `${filter.to}T23:59:59.999999999Z`);
  return p;
}

function buildHistoryQS(filter: HistoryFilter): string {
  const p = buildHistoryParams(filter);
  const page = filter.page ?? 1;
  p.set("limit", String(HISTORY_PAGE_SIZE));
  p.set("offset", String((page - 1) * HISTORY_PAGE_SIZE));
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchHistory(
  tenantID: string,
  filter: HistoryFilter,
): Promise<QueueResponse> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/queue${buildHistoryQS(filter)}`);
  return queueResponseSchema.parse(raw);
}

// historyQueryKey single-sources the cache key so the hook and the route loader
// can't drift (a drifted key silently double-fetches / misses the warm cache).
export const historyQueryKey = (tenantID: string, filter: HistoryFilter) =>
  ["access-governance", "history", tenantID, filter] as const;

export function useHistoryQuery(tenantID: string, filter: HistoryFilter) {
  return useQuery<QueueResponse, ApiError>({
    queryKey: historyQueryKey(tenantID, filter),
    queryFn: () => fetchHistory(tenantID, filter),
    enabled: tenantID.length > 0,
    // History is retrospective/immutable — no polling. A short stale-time keeps
    // back/forward navigation instant; keepPreviousData dims the page on paging.
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// historyExportURL builds a native-download URL for the filtered History set
// (the browser streams + downloads it, keeping memory flat). The export carries
// the SAME filter as the on-screen view, minus pagination — an export must be
// complete for the filter, not one page of it.
export function historyExportURL(
  tenantID: string,
  filter: HistoryFilter,
  format: "csv" | "ndjson",
): string {
  const p = buildHistoryParams(filter);
  p.set("format", format);
  return `${base(tenantID)}/export?${p.toString()}`;
}

export async function fetchJITHealth(tenantID: string) {
  const path = `/api/v1/tenants/${encodeURIComponent(tenantID)}/access-governance/health`;
  const raw = await apiFetch<unknown>(path);
  return jitHealthSchema.parse(raw);
}

// useJITHealthQuery — small status card on the queue page.
// Polls every 30s — the sweep timestamps update on a few-minute
// cadence, so a tighter poll buys nothing. Refetches on tab
// focus so a returning operator sees fresh state.
export function useJITHealthQuery(tenantID: string) {
  return useQuery({
    queryKey: ["access-governance", "health", tenantID],
    queryFn: () => fetchJITHealth(tenantID),
    enabled: tenantID.length > 0,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useQueueQuery(tenantID: string, filter: QueueFilter) {
  return useQuery<QueueResponse, ApiError>({
    queryKey: ["access-governance", "queue", tenantID, filter],
    queryFn: () => fetchQueue(tenantID, filter),
    enabled: tenantID.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// Activity-query keys + fetchers, exported so the route loader
// can prefetch the selected request's thread / decisions /
// attestations alongside the queue. Without prefetch the panel
// mounts in "loading" state and then collapses when data arrives,
// producing visible flicker on cold-cache deep links.
export const threadQueryKey = (tenantID: string, kind: RequestKind | "", requestID: string | null) =>
  ["access-governance", "thread", tenantID, kind, requestID] as const;

export const decisionsQueryKey = (tenantID: string, kind: RequestKind | "", requestID: string | null) =>
  ["access-governance", "decisions", tenantID, kind, requestID] as const;

export const attestationsQueryKey = (tenantID: string, kind: RequestKind | "", requestID: string | null) =>
  ["access-governance", "attestations", tenantID, kind, requestID] as const;

export async function fetchThread(tenantID: string, kind: RequestKind, requestID: string) {
  // Backend already returns the standard list envelope
  // `{data: ThreadComment[]}` — assert the shape directly.
  // The previous version wrapped raw a SECOND time as
  // `{data: raw}`, producing `{data: {data: [...]}}`. The
  // select() then ran `.map()` on the inner object and threw
  // "e.data.map is not a function" — manifesting in the UI
  // as "Failed to load discussion: e.data.map is not a function"
  // every time an approver opened a request's Discussion tab.
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/requests/${encodeURIComponent(kind)}/${encodeURIComponent(requestID)}/thread`,
  );
  return raw as { data: ThreadComment[] };
}

export async function fetchDecisions(tenantID: string, kind: RequestKind, requestID: string) {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/requests/${encodeURIComponent(kind)}/${encodeURIComponent(requestID)}/decisions`,
  );
  return raw as { data: DecisionRow[] };
}

export async function fetchAttestations(tenantID: string, kind: RequestKind, requestID: string) {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/requests/${encodeURIComponent(kind)}/${encodeURIComponent(requestID)}/attestations`,
  );
  return raw as { data: AttestationRow[] };
}

export function useThreadQuery(
  tenantID: string,
  kind: RequestKind | "",
  requestID: string | null,
) {
  return useQuery<{ data: ThreadComment[] }, ApiError>({
    queryKey: threadQueryKey(tenantID, kind, requestID),
    queryFn: () => fetchThread(tenantID, kind as RequestKind, requestID!),
    select: (out) => {
      const list = ((out as unknown) as { data: unknown[] }).data;
      return { data: list.map((c) => threadCommentSchema.parse(c)) };
    },
    enabled: tenantID.length > 0 && kind !== "" && Boolean(requestID),
    staleTime: 15_000,
    // While the detail panel is open, surface peer admins'
    // comments without requiring the operator to click around.
    refetchInterval: 30_000,
  });
}

export function useDecisionsQuery(
  tenantID: string,
  kind: RequestKind | "",
  requestID: string | null,
) {
  return useQuery<{ data: DecisionRow[] }, ApiError>({
    queryKey: decisionsQueryKey(tenantID, kind, requestID),
    queryFn: () => fetchDecisions(tenantID, kind as RequestKind, requestID!),
    select: (out) => {
      const list = ((out as unknown) as { data: unknown[] }).data;
      return { data: list.map((d) => decisionRowSchema.parse(d)) };
    },
    enabled: tenantID.length > 0 && kind !== "" && Boolean(requestID),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useAttestationsQuery(
  tenantID: string,
  kind: RequestKind | "",
  requestID: string | null,
) {
  return useQuery<{ data: AttestationRow[] }, ApiError>({
    queryKey: attestationsQueryKey(tenantID, kind, requestID),
    queryFn: () => fetchAttestations(tenantID, kind as RequestKind, requestID!),
    select: (out) => {
      const list = ((out as unknown) as { data: unknown[] }).data;
      return { data: list.map((a) => attestationRowSchema.parse(a)) };
    },
    enabled: tenantID.length > 0 && kind !== "" && Boolean(requestID),
    staleTime: 15_000,
  });
}

export interface PostCommentInput {
  kind: RequestKind;
  requestID: string;
  body: string;
  evidenceIDs?: string[];
}

export function usePostCommentMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string }, ApiError, PostCommentInput>({
    mutationFn: async (input) => {
      return apiFetch<{ id: string }>(
        `${base(tenantID)}/requests/${encodeURIComponent(input.kind)}/${encodeURIComponent(input.requestID)}/comment`,
        {
          method: "POST",
          body: { body: input.body, evidence_ids: input.evidenceIDs ?? [] },
        },
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [
          "access-governance",
          "thread",
          tenantID,
          vars.kind,
          vars.requestID,
        ],
      });
      qc.invalidateQueries({ queryKey: ["access-governance", "queue", tenantID] });
    },
  });
}

// ── Bulk decision ──────────────────────────────────────────────
//
// POST /identity/hygiene/access-requests/peer-decision-bulk applies
// the same decision to up to 50 access requests in one call. The
// backend returns per-row results (verdict OR error) so the UI can
// summarise "12 approved, 2 failed (one self-approval, one
// missing app)". Wraps in withStepUp because every approval
// commits an entitlement edge.

export interface BulkDecisionInput {
  requestIDs: string[];
  decision: DecisionKind;
  comment: string;
}

export interface BulkDecisionResult {
  request_id: string;
  verdict?: string;
  error?: string;
  error_code?: string;
}

export interface BulkDecisionResponse {
  data: BulkDecisionResult[];
}

export function useBulkDecisionMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<BulkDecisionResponse, ApiError, BulkDecisionInput>({
    mutationFn: async (input) => {
      const path = `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity/hygiene/access-requests/peer-decision-bulk`;
      return withStepUp(() =>
        apiFetch<BulkDecisionResponse>(path, {
          method: "POST",
          body: {
            request_ids: input.requestIDs,
            decision: input.decision,
            comment: input.comment,
          },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["access-governance", "queue", tenantID] });
    },
  });
}

// ── Manual mid-grant revoke ────────────────────────────────────
//
// POST /identity/hygiene/access-requests/{id}/revoke ends an
// active grant on demand — the audit-finding response path. Cancel
// only applies pre-approval; revoke applies to approved (active)
// grants. The endpoint deletes the entitlement edge, flips status
// to expired, and notifies the user.

export interface RevokeAccessInput {
  requestID: string;
  reason: string;
}

export function useRevokeAccessMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, RevokeAccessInput>({
    mutationFn: async (input) => {
      const path = `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity/hygiene/access-requests/${encodeURIComponent(input.requestID)}/revoke`;
      return withStepUp(() =>
        apiFetch<void>(path, {
          method: "POST",
          body: { reason: input.reason },
        }),
      );
    },
    onSuccess: () => {
      // Queue + decisions list both reflect the new status; the
      // detail panel reloads the row and re-renders as terminal.
      qc.invalidateQueries({ queryKey: ["access-governance", "queue", tenantID] });
    },
  });
}

// ── Force-escalate ──────────────────────────────────────────────────────────
// Widen a PENDING access request to the broader admin approver pool NOW — the same
// transition the timed escalation sweep performs, on demand (regardless of the SLA
// deadline). Never auto-approves. Lets an operator route a stuck request to more
// approvers immediately. Response: {outcome: "widened"|"unresolvable"|"skipped"}.

export interface EscalateRequestResult {
  outcome: string;
}

export function useEscalateRequestMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EscalateRequestResult, ApiError, { requestID: string }>({
    mutationFn: (input) => {
      const path = `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity/hygiene/access-requests/${encodeURIComponent(input.requestID)}/escalate`;
      return withStepUp(() => apiFetch<EscalateRequestResult>(path, { method: "POST", body: {} }));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["access-governance", "queue", tenantID] });
    },
  });
}

// ── Decision mutations — dispatch to the kind-specific endpoint
// based on RequestKind. Recovery + access have separate
// endpoints with different attestation requirements.

export interface DecisionInput {
  kind: RequestKind;
  requestID: string;
  decision: DecisionKind;
  comment: string;
  attestation?: {
    method: AttestationMethod;
    evidenceID?: string;
    notes?: string;
  };
  /** Approver-side duration override on access approvals. Server
   *  validates against the app's max_duration_minutes policy. */
  grantedDurationMinutes?: number;
}

export function useDecisionMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, DecisionInput>({
    mutationFn: async (input) => {
      const path =
        input.kind === "recovery"
          ? `/api/v1/tenants/${encodeURIComponent(tenantID)}/credential-recovery/requests/${encodeURIComponent(input.requestID)}/peer-decision`
          : `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity/hygiene/access-requests/${encodeURIComponent(input.requestID)}/peer-decision`;
      return withStepUp(() =>
        apiFetch<void>(path, {
          method: "POST",
          body: {
            decision: input.decision,
            comment: input.comment,
            attestation_method: input.attestation?.method,
            attestation_evidence_id: input.attestation?.evidenceID,
            attestation_notes: input.attestation?.notes,
            granted_duration_minutes:
              input.kind === "access" && input.decision === "approved"
                ? input.grantedDurationMinutes
                : undefined,
          },
        }),
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["access-governance", "queue", tenantID] });
      qc.invalidateQueries({
        queryKey: [
          "access-governance",
          "decisions",
          tenantID,
          vars.kind,
          vars.requestID,
        ],
      });
      qc.invalidateQueries({
        queryKey: [
          "access-governance",
          "thread",
          tenantID,
          vars.kind,
          vars.requestID,
        ],
      });
    },
  });
}

// ── Platform override (break-glass) ───────────────────────────

// PlatformOverrideInput — the wire shape for the force-decide
// modal. Verdict is approve | deny only ("needs_info" is invalid
// for an override, which is by definition final). Reason is
// auditor-readable, ≥20 chars per the backend CHECK.
export interface PlatformOverrideInput {
  kind: RequestKind;
  requestID: string;
  verdict: "approved" | "denied";
  reason: string;
}

// usePlatformOverrideMutation force-decides a stuck request via
// the break-glass endpoint. Different surface from
// useDecisionMutation:
//
//   * Different endpoint
//     (/access-governance/queue/{kind}/{id}/platform-override)
//   * Decision row marked is_platform_override=true and skipped
//     by the N-of-M aggregator
//   * Fan-out notification fires on the backend (every active
//     tenant admin + every grant holder receives an email)
//
// withStepUp wrap because the backend gates the route under
// PermAccessGovernancePlatformOverride + step-up. The reason
// length floor (20 chars) is enforced both client-side in the
// form and server-side in the handler.
export function usePlatformOverrideMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, PlatformOverrideInput>({
    mutationFn: async (input) => {
      const path = `/api/v1/tenants/${encodeURIComponent(tenantID)}/access-governance/queue/${encodeURIComponent(input.kind)}/${encodeURIComponent(input.requestID)}/platform-override`;
      return withStepUp(() =>
        apiFetch<void>(path, {
          method: "POST",
          body: {
            verdict: input.verdict,
            reason: input.reason,
          },
        }),
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["access-governance", "queue", tenantID] });
      qc.invalidateQueries({
        queryKey: [
          "access-governance",
          "decisions",
          tenantID,
          vars.kind,
          vars.requestID,
        ],
      });
      qc.invalidateQueries({
        queryKey: [
          "access-governance",
          "thread",
          tenantID,
          vars.kind,
          vars.requestID,
        ],
      });
    },
  });
}
